import { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Zap, Circle, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 100-point inspection report with Ready-to-Drive score + Carfax section.
 * Props: vehicleId
 */
export default function InspectionReport({ vehicleId }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const isEn = i18n.language?.startsWith('en');

  useEffect(() => {
    if (!vehicleId) return;
    axios.get(`${API}/vehicles/${vehicleId}/inspection`).then((res) => setData(res.data)).catch(() => {});
  }, [vehicleId]);

  if (!data?.report) return null;
  const r = data.report;

  const dot = (pct) => {
    if (pct >= 95) return 'bg-emerald-500';
    if (pct >= 85) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="inspection-report">
      <div className="flex items-center gap-2 mb-4">
        <Award className="h-6 w-6 text-emerald-600" />
        <h2 className="text-xl font-black text-slate-900">{t('carvana.inspection.title', 'Rapport d\'inspection 100 points')}</h2>
      </div>

      {/* Ready to Drive Score */}
      <div className="bg-gradient-to-r from-emerald-50 to-white border border-emerald-200 rounded-xl p-5 mb-6">
        <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-1">{t('carvana.inspection.readyToDrive', 'Score prêt à rouler')}</p>
        <div className="flex items-baseline gap-3">
          <span className="text-5xl font-black text-emerald-600" data-testid="ready-to-drive-pct">{r.ready_to_drive_pct}%</span>
          <span className="text-sm text-slate-600">{r.total_points_earned}/{r.total_points_max} {t('carvana.inspection.points', 'points')}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
          <div className="text-center bg-white rounded-lg p-2 border border-slate-100">
            <Circle className="h-4 w-4 mx-auto mb-1 text-slate-500" />
            <p className="text-[10px] text-slate-500 uppercase">{t('carvana.inspection.tires', 'Pneus')}</p>
            <p className="font-bold text-slate-900" data-testid="tires-pct">{r.highlights?.tires_pct}%</p>
          </div>
          <div className="text-center bg-white rounded-lg p-2 border border-slate-100">
            <ShieldCheck className="h-4 w-4 mx-auto mb-1 text-slate-500" />
            <p className="text-[10px] text-slate-500 uppercase">{t('carvana.inspection.brakes', 'Freins')}</p>
            <p className="font-bold text-slate-900" data-testid="brakes-pct">{r.highlights?.brakes_pct}%</p>
          </div>
          <div className="text-center bg-white rounded-lg p-2 border border-slate-100">
            <Zap className="h-4 w-4 mx-auto mb-1 text-slate-500" />
            <p className="text-[10px] text-slate-500 uppercase">{t('carvana.inspection.battery', 'Batterie')}</p>
            <p className="font-bold text-slate-900" data-testid="battery-pct">{r.highlights?.battery_pct}%</p>
          </div>
        </div>
      </div>

      {/* Full checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(r.categories || []).map((c) => (
          <div key={c.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${dot(c.score_pct)}`} />
              <span className="text-slate-700">{isEn ? c.label_en : c.label_fr}</span>
            </div>
            <span className="font-bold text-slate-900">{c.score_pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
